<template>
  <div class="container v-content-container tooltip-wrapper popup-wrapper">
    <div class="row tooltip-wrapper">
      <div class="col-xs-12">
        <h1>
          <i class="fa fa-user fa-fw fa-users"></i>
          <span v-if="selectedGroupId != null">
            {{ noDataExist ? "Group" : originalName }}
          </span>
          <span v-else-if="!loading">
            {{ $label("group.new_group", "New customer group") }}
          </span>

          <group-actions :group-id="selectedGroupId"></group-actions>
        </h1>
      </div>
    </div>

    <div v-show="loading" class="row">
      <div class="col-xs-12">
        <view-loader
          margin-top-pixels="15"
          border-color="primary"
        ></view-loader>
      </div>
    </div>

    <div class="row"><div class="col-xs-12">&nbsp;</div></div>

    <error404 v-if="noDataExist" />

    <template v-else>
      <div class="row">
        <div class="col-sm-6">
          <h2>{{ $label("group.group", "Group") }}</h2>
          <p></p>
          <div class="v-form-partial">
            <div class="v-form">
              <fieldset>
                <!-- BEGIN Group name -->
                <section>
                  <label class="label">{{
                    $label("group.name", "Name")
                  }}</label>
                  <label class="input">
                    <i class="icon-append icon-user"></i>
                    <input
                      v-model="selectedGroup.name"
                      :class="{ 'text-input-error': hasError('name') }"
                      :placeholder="$label('group.name', 'Name')"
                      :disabled="updating"
                      :readonly="originalName != null"
                      id="group-name"
                      @keyup.enter="createOrUpdateGroup"
                    />
                  </label>
                  <label v-show="originalName != null" class="note"
                    ><i class="text-primary fa fa-lg fa-info-circle"></i>
                    {{ $label("group.the_group_name", "The group name") }} "{{
                      selectedGroup.name
                    }}"
                    {{
                      $label("group.cannot_be_changed", "cannot be changed")
                    }}.</label
                  >

                  <p v-if="hasError('name')" class="text-danger">
                    {{ getError("name") }}
                  </p>
                </section>
                <!-- END Group name -->

                <!-- BEGIN Group description -->
                <section>
                  <label class="label">{{
                    $label("group.description", "Internal description")
                  }}</label>
                  <label class="textarea">
                    <i class="icon-append icon-info-sign"></i>
                    <textarea
                      v-model="selectedGroup.description"
                      :class="{ 'text-input-error': hasError('description') }"
                      :placeholder="
                        $label(
                          'group.internal_description',
                          'Internal description'
                        )
                      "
                      :disabled="updating"
                      id="group-description"
                      rows="8"
                    >
                    </textarea>
                  </label>
                </section>
                <!-- END Group description -->
              </fieldset>
            </div>
          </div>
        </div>

        <div class="col-sm-6">
          <h2>{{ $label("group.members", "Members") }}</h2>
          <p></p>
          <div class="v-form-partial">
            <div class="table-responsive table-group-customers">
              <table
                class="table table-striped table-hover"
                id="table-customers"
              >
                <thead>
                  <tr>
                    <th>
                      {{ $label("group.group_members", "Group members") }}
                    </th>
                    <th class="table-customers-type text-center">
                      {{ $label("common.type", "Type") }}
                    </th>
                    <th class="text-center table-group-customers-actions">
                      {{ $label("common.actions", "Actions") }}
                    </th>
                  </tr>
                </thead>
                <tbody v-show="selectedGroupId != null">
                  <tr v-for="customer in customers" v-bind:key="customer.id">
                    <td class="v-table sorting_1">
                      {{
                        beautifyName(customer.first_name, customer.last_name)
                      }}
                    </td>
                    <td class="v-table" align="center">Type</td>
                    <td class="v-table" align="center">
                      <div class="btn-group">
                        <router-link
                          :to="{
                            name: 'CustomerData',
                            params: { customer_id: customer.id }
                          }"
                          v-tooltip="{
                            content: $label('common.details', 'Details'),
                            classes: 'custom-tooltip'
                          }"
                          data-id="customerId"
                          class="btn btn-default btn-default-sm"
                          ><i class="text-primary fa fa-lg fa-fw fa-eye"></i>
                          {{ $label("common.details", "Details") }}</router-link
                        >
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-show="selectedGroupId != null">
              <div
                class="dataTables_paginate paging_bootstrap_full_number"
                style="text-align: right; margin-right: 10px;"
              >
                <ul class="pagination mt-0" style="visibility: visible;">
                  <li class="prev" :class="{ disabled: disabledFirstPage }">
                    <a
                      @click="goToFirstPage"
                      v-tooltip="{
                        content: $label('common.first_page', 'First page'),
                        classes: 'custom-tooltip'
                      }"
                      ><i class="fa fa-angle-double-left"></i
                    ></a>
                  </li>
                  <li class="prev" :class="{ disabled: disabledPreviousPage }">
                    <a
                      @click="goToPreviousPage"
                      v-tooltip="{
                        content: $label('common.back'),
                        classes: 'custom-tooltip'
                      }"
                      ><i class="fa fa-angle-left"></i
                    ></a>
                  </li>
                  <li class="active">
                    <a>{{ currentPage }}</a>
                  </li>
                  <li class="next" :class="{ disabled: disabledNextPage }">
                    <a
                      @click="goToNextPage"
                      v-tooltip="{
                        content: $label('common.next', 'Next'),
                        classes: 'custom-tooltip'
                      }"
                      ><i class="fa fa-angle-right"></i
                    ></a>
                  </li>
                  <li class="next" :class="{ disabled: disabledLastPage }">
                    <a
                      @click="goToLastPage"
                      v-tooltip="{
                        content: $label('common.last_page', 'Last page'),
                        classes: 'custom-tooltip'
                      }"
                      ><i class="fa fa-angle-double-right"></i
                    ></a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-sm-6">
          <h2>Online-Buchung</h2>
          <p></p>
          <div class="v-form-partial">
            <div class="v-form">
              <fieldset>
                <div class="row">
                  <section class="col col-6">
                    <label class="label"
                      >Im Voraus buchbar bis zu
                      <i
                        v-tooltip.top-center="
                          '<div class=&quot;tooltip-title-background&quot;><p class=&quot;tooltip-title&quot;><i class=&quot;text-primary fa fa-lg fa-fw fa-info-circle&quot;></i><b>Buchungszeitraum Kundengruppe</b></p></div>' +
                            '<p class=&quot;tooltip-content&quot;>Für jede Kundengruppe kann ein von den allgemeinen Einstellungen abweichender Buchungszeitraum definiert werden.</p>'
                        "
                        class="text-primary fa fa-lg fa-info-circle pull-right"
                      ></i
                    ></label>
                    <label class="select">
                      <select name="online_max_booking_days">
                        <option value="" selected
                          >Allgemeine Einstellung</option
                        >
                        <option value="1">24 Stunden</option>
                        <option value="2">2 Tage</option>
                        <option value="3">3 Tage</option>
                        <option value="7">1 Woche</option>
                        <option value="14">2 Wochen</option>
                        <option value="30">1 Monat</option>
                        <option value="60">2 Monate</option>
                        <option value="91">3 Monate</option>
                        <option value="121">4 Monate</option>
                        <option value="152">5 Monate</option>
                        <option value="182">6 Monate</option>
                        <option value="365">12 Monate</option>
                      </select>
                      <i></i>
                    </label>
                    <div class="note">
                      <a href="/settings/online/venues/"
                        >Allgemeine Online-Einstellungen</a
                      >
                    </div>
                  </section>
                  <section class="col col-6">
                    <label class="label"
                      >Offene Buchungen pro Kunde
                      <i
                        v-tooltip.top-center="
                          '<div class=&quot;tooltip-title-background&quot;><p class=&quot;tooltip-title&quot;><i class=&quot;text-primary fa fa-lg fa-fw fa-info-circle&quot;></i><b>Was bewirkt diese Einstellung?</b></p></div>' +
                            '<p class=&quot;tooltip-content&quot;>Wählt man die Einstellung <i>Standard</i>, so gilt hier die allgemeine Online-Einstellung.<br><br>' +
                            'Für die Gruppe kann man aber eine abweichende Anzahl von noch kommenden Buchungen definieren. Ist der Kunde in mehreren Gruppen, so gilt die grösste Einstellung.<br><br>' +
                            'Die Gruppeneinstellung übersteuert die allgemeinen Online-Einstellungen.</p>'
                        "
                        class="text-primary fa fa-lg fa-info-circle pull-right"
                      ></i
                    ></label>
                    <label class="select">
                      <select name="online_num_open_bookings">
                        <option value="" selected
                          >Allgemeine Einstellung</option
                        >
                        <option value="1">Eine offene Buchung</option>
                        <option value="2">Zwei offene Buchungen</option>
                        <option value="3">Drei offene Buchungen</option>
                        <option value="4">Vier offene Buchungen</option>
                        <option value="5">Fünf offene Buchungen</option>
                        <option value="10">Zehn offene Buchungen</option>
                        <option value="unlimited">Unbegrenzt</option>
                      </select>
                      <i></i>
                    </label>
                    <div class="note">
                      <a href="/settings/online/venues/"
                        >Allgemeine Online-Einstellungen</a
                      >
                    </div>
                  </section>
                </div>

                <div class="row">
                  <section class="col col-6">
                    <label class="label"
                      >Kurse früher buchen
                      <i
                        v-tooltip.top-center="
                          '<div class=&quot;tooltip-title-background&quot;><p class=&quot;tooltip-title&quot;><i class=&quot;text-primary fa fa-lg fa-fw fa-info-circle&quot;></i><b>Kursbuchungsbeginn für Kundengruppe</b></p></div>' +
                            '<p class=&quot;tooltip-content&quot;>Mit dieser Einstellung kann man festlegen, um wieviel früher als normale Kunden jemand aus dieser Kundengruppe buchen darf.<br><br>' +
                            'Wenn der Beginn der Kursbuchung z.B. auf 23.11. gesetzt ist und diese Gruppe 2 Wochen früher buchen darf, ist der 9.11. deren Beginn.</p>'
                        "
                        class="text-primary fa fa-lg fa-info-circle pull-right"
                      ></i
                    ></label>
                    <label class="select">
                      <select name="online_max_course_booking_days">
                        <option value="" selected>normaler Kursbeginn</option>
                        <option value="7">1 Woche</option>
                        <option value="14">2 Wochen</option>
                        <option value="30">1 Monat</option>
                        <option value="60">2 Monate</option>
                        <option value="91">3 Monate</option>
                        <option value="121">4 Monate</option>
                        <option value="152">5 Monate</option>
                        <option value="182">6 Monate</option>
                        <option value="365">12 Monate</option> </select
                      ><i></i>
                    </label>
                  </section>
                </div>
              </fieldset>
            </div>
          </div>
        </div>
        <div class="col-sm-6">
          <h2>Abrechnungsoptionen</h2>
          <p></p>
          <div class="v-form-partial">
            <div class="v-form">
              <fieldset>
                <div class="row">
                  <section class="col col-10">
                    <label class="label">
                      Venue abrechnen durch:
                      <i
                        v-tooltip.top-center="
                          '<div class=&quot;tooltip-title-background&quot;><p class=&quot;tooltip-title&quot;><i class=&quot;text-primary fa fa-lg fa-fw fa-info-circle&quot;></i><b>' +
                            $label(
                              'group.venue_accounting',
                              'Venue accounting'
                            ) +
                            '</b></p></div>' +
                            '<p class=&quot;tooltip-content&quot;>' +
                            $label(
                              'group.venue_account_by_tooltip',
                              'Venue bookings are usually based on the invoicing settings of the location.<br><br>With this dropdown a special operator can be set for customers of this customer group, which is then used permanently'
                            ) +
                            '</p>'
                        "
                        class="text-primary fa fa-lg fa-info-circle pull-right"
                      ></i
                    ></label>
                    <label class="select">
                      <select name="venue_operator_purpose">
                        <option value="">Standard-Einstellung verwenden</option>
                        <option value="1"> venueOperatorName</option>
                      </select>
                      <i></i>
                    </label>
                  </section>
                  <section class="col col-2">
                    <label class="label">&nbsp;</label>
                    <a
                      href="/settings/operator/facility/new/"
                      class="btn btn-primary v-form-button v-white"
                      v-tooltip="{
                        content: $label(
                          'facility.create_new_facility',
                          'Create new facility'
                        ),
                        classes: 'custom-tooltip'
                      }"
                    >
                      <i class="fa fa-lg fa-home"></i><i class="fa fa-plus"></i>
                    </a>
                  </section>
                </div>
              </fieldset>
            </div>
          </div>
        </div>
      </div>

      <div class="row"><div class="col-xs-12">&nbsp;</div></div>
      <hr />

      <div class="row">
        <div class="col-xs-3 text-center">&nbsp;</div>

        <div class="col-xs-6 text-center">
          <back-button
            back-link="GroupsOverview"
            :class="{ 'is-disabled': updating }"
            id="back-group-button"
            icon="fa fa-lg fa-fw fa-arrow-circle-o-left"
            :text="$label('common.back', 'Back')"
          >
          </back-button>
          &nbsp;
          <info-button
            :class="{ 'is-disabled': updating }"
            id="save-group-button"
            icon="fa fa-lg fa-fw fa-save"
            :text="$label('common.save', 'Save')"
            @click.native="createOrUpdateGroup"
          >
          </info-button>
          &nbsp;
          <danger-button
            :class="{ 'is-disabled': updating || selectedGroupId == null }"
            id="delete-group-button"
            icon="fa fa-fw fa-lg fa-trash-o"
            :text="$label('common.delete', 'Delete')"
            @click.native="deleteGroup"
          >
          </danger-button>
        </div>

        <div class="col-xs-3 text-center">&nbsp;</div>
      </div>

      <div class="row"><div class="col-xs-12">&nbsp;</div></div>

      <div class="row mb-4">
        <div class="col-xs-12"></div>
      </div>
    </template>
  </div>
</template>

<script>
import swal from "sweetalert";
import Api from "@/api";

import FormErrorsMixin from "@/mixins/FormErrors";
import ViewLoader from "@/components/Common/ViewLoader";
import InfoButton from "@/components/Common/Buttons/InfoButton";
import BackButton from "@/components/Common/Buttons/BackButton";
import DangerButton from "@/components/Common/Buttons/DangerButton";
import GroupActions from "@/components/Settings/Groups/GroupActions";
import Error404 from "@/components/Common/Error404";

export default {
  name: "GroupDetails",

  mixins: [FormErrorsMixin],

  components: {
    ViewLoader,
    GroupActions,
    InfoButton,
    BackButton,
    DangerButton,
    Error404
  },

  data() {
    return {
      loading: false,
      updating: false,
      noDataExist: false,
      originalName: null,
      selectedGroup: {
        name: null,
        description: null,
        locked: null
      },
      selectedGroupId: null,
      failedRequest: false,
      validatedInputs: ["name", "description"],
      // customers data
      customers: [],
      customersLength: null,
      currentPage: 1,
      total: 0,
      from: 0,
      to: 0,
      firstPage: 1,
      lastPage: null,
      perPage: null,
      searchFilter: ""
    };
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.selectedGroupId = vm.$route.params.group_id;
      if (
        typeof vm.selectedGroupId !== "undefined" &&
        vm.selectedGroupId !== null
      ) {
        if (vm.$route.query.group_created) {
          swal("Success!", "The group was created.", "success").then(() => {
            window.history.pushState(
              {},
              document.title,
              window.location.pathname
            );
          });
        }
        vm.getGroup(() => {
          //
        });
        vm.getGroupCustomers();
      }
    });
  },

  beforeRouteUpdate(to, from, next) {
    if (
      typeof this.selectedGroupId !== "undefined" &&
      this.selectedGroupId !== null
    ) {
      this.getGroup(() => {
        //
      });
      this.getGroupCustomers();
    }
    next();
  },

  mounted() {
    //
  },

  methods: {
    createOrUpdateGroup() {
      if (this.selectedGroupId == null) {
        this.createGroup();
      } else {
        this.updateGroup();
      }
    },

    createGroup() {
      if (this.updating) {
        return;
      }

      this.resetErrors();
      this.updating = true;
      const endpoint = this.$partnerEndpoint("groups");

      Api()
        .post(endpoint, this.selectedGroup)
        .then(response => {
          this.selectedGroupId = response.data.data.id;
          this.$router.push({
            name: "GroupDetails",
            params: { group_id: this.selectedGroupId },
            query: { group_created: true }
          });
        })
        .catch(error => {
          this.storeErrors(error.response.data.errors);
          this.$scrollTo("#group-name", 500);
        })
        .finally(() => {
          this.updating = false;
        });
    },

    getGroup() {
      if (this.loading) {
        return;
      }

      this.resetErrors();
      this.loading = true;
      const endpoint = this.$partnerEndpoint("groups/" + this.selectedGroupId);

      Api()
        .get(endpoint)
        .then(response => {
          this.selectedGroup = response.data.data;
          this.originalName = this.selectedGroup.name;
        })
        .catch(error => {
          if (error.response.status === 404) {
            this.noDataExist = true;
          }
          console.log(error.response);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    updateGroup() {
      if (this.loading || this.updating) {
        return;
      }

      this.resetErrors();
      this.updating = true;
      const endpoint = this.$partnerEndpoint("groups/" + this.selectedGroupId);

      Api()
        .put(endpoint, this.selectedGroup)
        .then(() => {
          this.getGroup(() => {
            //
          });
          swal({
            title: "Success!",
            text: "The group was updated.",
            icon: "success"
          });
        })
        .catch(error => {
          this.storeErrors(error.response.data.errors);
          this.$scrollTo("#group-name", 500);
        })
        .finally(() => {
          this.updating = false;
        });
    },

    deleteGroup() {
      if (this.loading || this.updating) {
        return;
      }

      swal({
        title: "Are you sure?",
        text: "The group will be deleted",
        icon: "warning",
        dangerMode: true,
        buttons: {
          cancel: {
            visible: true,
            text: "Cancel",
            closeModal: true
          },
          confirm: {
            visible: true,
            text: "Yes, delete it!",
            closeModal: true
          }
        }
      }).then(isConfirm => {
        if (isConfirm) {
          const endpoint = this.$partnerEndpoint(
            "groups/" + this.selectedGroupId
          );

          Api()
            .delete(endpoint)
            .then(() => {
              this.$router.push({
                name: "GroupsOverview",
                query: { group_deleted: true }
              });
            })
            .catch(error => {
              if (error.response.status === 403) {
                swal({
                  title: "Missing permission",
                  text: "You are not allowed to delete groups.",
                  icon: "error"
                  //dangerMode: true,
                });
              }
            });
        } else {
          //
        }
      });
    },

    getGroupCustomers(successCallback) {
      this.customers = [];
      const endpoint = this.$partnerEndpoint(
        "groups/" + this.selectedGroupId + "/customers"
      );

      Api()
        .get(endpoint, {
          params: { page: this.currentPage, search: this.searchFilter }
        })
        .then(response => {
          this.customers = response.data.data;
          this.currentPage = response.data.meta.current_page;
          this.total = response.data.meta.total;
          this.from = response.data.meta.from;
          this.to = response.data.meta.to;
          this.lastPage = response.data.meta.last_page;
          this.perPage = response.data.meta.per_page;
          this.customersLength = this.customers.length;
          this.failedRequest = false;
          if (typeof successCallback == "function") {
            successCallback();
          }
        })
        .catch(() => {
          this.failedRequest = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    goToFirstPage() {
      if (!this.disabledFirstPage) {
        this.currentPage = 1;
        this.getGroupCustomers(() => {
          //
        });
      }
    },

    goToPreviousPage() {
      if (!this.disabledPreviousPage) {
        this.currentPage--;
        this.getGroupCustomers(() => {
          //
        });
      }
    },

    goToNextPage() {
      if (!this.disabledNextPage) {
        this.currentPage++;
        this.getGroupCustomers(() => {
          //
        });
      }
    },

    goToLastPage() {
      if (!this.disabledLastPage) {
        this.currentPage = this.lastPage;
        this.getGroupCustomers(() => {
          //
        });
      }
    }
  },

  computed: {
    disabledFirstPage() {
      return this.currentPage === 1;
    },

    disabledPreviousPage() {
      return this.currentPage === 1;
    },

    disabledNextPage() {
      return this.currentPage === this.lastPage;
    },

    disabledLastPage() {
      return this.currentPage === this.lastPage;
    }
  }
};
</script>

<style scoped>
.mb-4 {
  margin-bottom: 1.2rem;
}

.mt-0 {
  margin-top: 0rem;
}

.mt-8 {
  margin-top: 1.3rem;
}

.mb-0 {
  margin-bottom: 0rem;
}

.mb-1 {
  margin-bottom: 0.3rem;
}

.mb-2 {
  margin-bottom: 0.6rem;
}

.mb-8 {
  margin-bottom: 1.3rem;
}

.min-width-auto {
  width: 50px;
}
</style>
